import React, {FC, useState, useRef} from "react";

import {Table, Space, Modal, Dropdown, Menu, Button, message} from 'antd';
import { ProColumns, ActionType } from '@ant-design/pro-table';
import ProTable from '@ant-design/pro-table';
import type { ProDescriptionsActionType } from "@ant-design/pro-descriptions";

import {DownOutlined} from '@ant-design/icons';

import type {Pagination} from './../../../../utils/data/Type';
import TableUtils from './../../../../utils/ui/table/TableUtils';
import { StaffPosition } from './../../type/StaffPositionType';
import StaffPositionApi from "../../api/StaffPositionApi";
import StaffPositionAddForm from "./StaffPositionAddForm";
import StaffPositionDetail from "./StaffPositionDetail";

const StaffPositionPage:React.FC = ()=>{

    const tableActionRef = useRef<ActionType>();
    const detailActionRef = useRef<ProDescriptionsActionType>();

    const [addFormVisible, setAddFormVisible] = useState<boolean>(false);
    const [detailVisible, setDetailVisible] = useState<boolean>(false);
    const [currentStaffPosition, setCurrentStaffPosition] = useState<StaffPosition>({});

    const refreshData = ()=>{
        tableActionRef.current?.reload();
    }

    const deleteStaffPosition = (staffPosition:StaffPosition)=>{
        Modal.confirm({
            title: "删除角色确认",
            content: "确定删除该角色吗？",
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                await StaffPositionApi.deleteStaffPosition(staffPosition);
                tableActionRef.current?.reload();
            }
        });
    }

    const showAddForm = ()=>{
        setAddFormVisible(true);
    }

    const hideAddForm = ()=>{
        setAddFormVisible(false);
    }

    const showDetail = (staffPosition:StaffPosition)=>{
        setCurrentStaffPosition(staffPosition);
        setDetailVisible(true);
    }

    const hideDetail = ()=>{
        setCurrentStaffPosition({});
        setDetailVisible(false);
    }

    const rowExtraButtons : React.FC<{
        item: StaffPosition
    }> = ({item}) => {
        return (
            <Dropdown
                key="row-extra-btns"
                overlay={
                    <Menu
                        onClick={({key})=>{
                            if(key==='delete') {
                                deleteStaffPosition(item);
                            }
                        }}
                    >
                        <Menu.Item key="delete">
                            删除
                        </Menu.Item>
                    </Menu>
                }
            >
                <a>
                    更多 <DownOutlined />
                </a>
            </Dropdown>
        );
    };

    const columns:ProColumns<StaffPosition>[] = [
        {
            key: "rowNo",
            title: "序号",
            dataIndex: "rowNo",
            width: 60,
            hideInSearch: true,
            hideInSetting: true,
            hideInForm: true,
            copyable: false,
        },
        {
            key: "orgId",
            title: "",
            dataIndex: "orgId",
            width: 180,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "postId",
            title: "",
            dataIndex: "postId",
            width: 180,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            key: "staffId",
            title: "",
            dataIndex: "staffId",
            width: 180,
            hideInTable: false,
            hideInSearch: false,
            hideInSetting: false,
            hideInForm: false,
            copyable: false,
        },
        {
            title: '操作',
            valueType: 'option',
            dataIndex: 'actions',
            render: (text, record:StaffPosition, _, action) =>{
                return [
                    <a key='view' onClick={()=>{
                        setCurrentStaffPosition(record);
                        setDetailVisible(true);
                        }}>
                        查看
                    </a>,
                    rowExtraButtons({item:record}),
                ];
            }
        },
    ];

    return (
        <div>
            <ProTable<StaffPosition, Pagination>
                headerTitle="角色列表"
                rowKey="id"
                columns={columns}
                actionRef={tableActionRef}
                scroll={{x: TableUtils.getTableScrollX(columns)}}
                rowSelection={{
                    alwaysShowAlert:true,
                    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
                    defaultSelectedRowKeys: [],
                }}
                tableAlertRender={({ selectedRowKeys, selectedRows, onCleanSelected }) => (
                    <Space size={24}>
                          <span>
                            已选 {selectedRowKeys.length} 项
                            <a style={{ marginLeft: 8 }} onClick={onCleanSelected}>
                              取消选择
                            </a>
                          </span>
                    </Space>
                )}
                tableAlertOptionRender={({selectedRowKeys}) => {
                    return (
                        <Space size={16}>
                            <a onClick={async ()=>{
                                Modal.confirm({
                                    title: '删除角色',
                                    content: '确认删除所选角色吗？',
                                    okText: '删除所选',
                                    cancelText: '取消',
                                    onOk:async ()=>{
                                        const hide = message.loading("数据删除中....");
                                        for(let i=0; i<selectedRowKeys.length; i++){
                                            let id:string = selectedRowKeys[i]+"";
                                            try{
                                                await StaffPositionApi.deleteStaffPosition({id:id});
                                            }catch(ex){

                                            }
                                        }
                                        hide();
                                        tableActionRef.current?.reload();
                                        message.success("数据删除完成！");
                                    }
                                });
                            }}>
                                批量删除
                            </a>
                        </Space>
                    );
                }}
                toolBarRender={()=>{
                    return [
                        <Button
                            type="primary"
                            onClick={showAddForm}
                        >
                            新建
                        </Button>
                    ];
                }}
                request={async (params={}, sort, filter)=>{

                    params.page = params.current;
                    const pageInfo = await StaffPositionApi.findStaffPositionPage({
                        deletedFlag: false,
                        ...params,
                    });

                    pageInfo.list.forEach((item:any, idx:number)=>{
                        item.rowNo = ((params.current||1)-1)*(params.pageSize||20)+idx+1;
                    });

                    return {
                        success: true,
                        data: pageInfo.list,
                    };
                }}
            />

            <StaffPositionAddForm
                visible={addFormVisible}
                onFail={hideAddForm}
                onSuccess={refreshData}
            />

            <StaffPositionDetail
                visible={detailVisible}
                staffPosition={currentStaffPosition}
                actionRef={detailActionRef}
                onFail={hideDetail}
                onSuccess={refreshData}
            />
        </div>
    );
};

export default StaffPositionPage;